import React, { useLayoutEffect, useMemo, useState } from 'react';
import { Box, Toolbar, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from './Header';
import Sidebar from './Sidebar';
import { useMotionPresets } from '../../theme/motion';
import ErrorBoundary from '../Common/ErrorBoundary';
import AssistantWidget from '../Assistant/AssistantWidget';

const DRAWER_EXPANDED = 240;
const DRAWER_COLLAPSED = 72;
const LS_SIDEBAR_EXPANDED_KEY = 'hr_sidebar_expanded_v1';

function MainLayout() {
  const theme = useTheme();
  const location = useLocation();
  const motionPresets = useMotionPresets();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const hrPageGradient = theme.hr?.gradient?.page;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopExpanded, setDesktopExpanded] = useState(() => {
    const stored = localStorage.getItem(LS_SIDEBAR_EXPANDED_KEY);
    if (stored === '0') return false;
    if (stored === '1') return true;
    return true;
  });

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
    } else {
      setDesktopExpanded((prev) => {
        const next = !prev;
        localStorage.setItem(LS_SIDEBAR_EXPANDED_KEY, next ? '1' : '0');
        return next;
      });
    }
  };

  useLayoutEffect(() => {
    // Avoid "blank until scroll" flashes by resetting scroll before paint.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  const layoutBackground = useMemo(
    () => {
      const primary = theme.palette.primary.main;
      const secondary = theme.palette.secondary.main;
      const accent = theme.palette.success.main;

      return {
        background: hrPageGradient || (theme.palette.mode === 'dark'
            ? `radial-gradient(1200px 360px at 0% -10%, ${alpha(secondary, 0.14)}, transparent 55%), radial-gradient(900px 320px at 100% -14%, ${alpha(primary, 0.2)}, transparent 58%), radial-gradient(720px 260px at 58% -12%, ${alpha(accent, 0.1)}, transparent 64%), linear-gradient(180deg, #0c1220 0%, #0f1627 100%)`
            : `radial-gradient(1200px 360px at 0% -10%, ${alpha(secondary, 0.09)}, transparent 55%), radial-gradient(900px 320px at 100% -14%, ${alpha(primary, 0.11)}, transparent 58%), radial-gradient(720px 260px at 58% -12%, ${alpha(accent, 0.08)}, transparent 64%), linear-gradient(180deg, #f4f6fb 0%, #eef3fa 100%)`),
      };
    },
    [hrPageGradient, theme.palette.mode, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main]
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', ...layoutBackground }}>
      <Header
        onMenuClick={handleDrawerToggle}
        isMobile={isMobile}
        isExpanded={desktopExpanded}
        drawerWidthExpanded={DRAWER_EXPANDED}
        drawerWidthCollapsed={DRAWER_COLLAPSED}
      />

      <Sidebar
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        expanded={desktopExpanded}
        drawerWidthExpanded={DRAWER_EXPANDED}
        drawerWidthCollapsed={DRAWER_COLLAPSED}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 0.6, sm: 1.5, md: 2.2 },
          pb: { xs: 1.5, sm: 2.2 },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }} />

        <ErrorBoundary resetKey={location.pathname}>
          <motion.div
            key={location.pathname}
            // Avoid route-level initial hidden state; it can manifest as a "blank screen"
            // on some GPUs/browsers until a repaint happens.
            initial={false}
            animate={motionPresets.fadeUp.animate}
            transition={motionPresets.fadeUp.transition}
          >
            <Outlet />
          </motion.div>
        </ErrorBoundary>
      </Box>

      {/* Unified HR assistant (Claude-powered: personal data, how-to, and
          policy-document Q&A) — self-hides when the integration is disabled */}
      <AssistantWidget />
    </Box>
  );
}

export default MainLayout;
